import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Calendar, Clock, Tag, Pin } from 'lucide-react';
import { caseService, caseNotesService, searchClientsAsync } from '../../services/api';
import AsyncSearchableSelect from '../common/AsyncSearchableSelect';

const QuickAddNoteModal = ({ isOpen, onClose, onSuccess }) => {
  const location = useLocation();
  const contentRef = useRef(null);

  const [cases, setCases] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClientLabel, setSelectedClientLabel] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [form, setForm] = useState({
    event_date: '',
    event_time: '',
    include_time: false,
    title: '',
    content: '',
    note_type: 'general',
    is_pinned: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadingCases, setLoadingCases] = useState(false);
  const [autoDetected, setAutoDetected] = useState(false);

  const noteTypes = caseNotesService.getNoteTypes();

  // Auto-detect case context from URL
  useEffect(() => {
    if (!isOpen || autoDetected) return;
    const match = location.pathname.match(/^\/cases\/([^/]+)/);
    if (match) {
      const caseId = match[1];
      const loadCaseContext = async () => {
        try {
          const caseRes = await caseService.getCase(caseId);
          const caseData = caseRes.data;
          if (caseData.client_id) {
            setSelectedClientId(caseData.client_id);
            const clientName = caseData.client_name || caseData.client?.first_name
              ? `${caseData.client?.first_name || ''} ${caseData.client?.last_name || ''}`.trim()
              : null;
            setSelectedClientLabel(clientName);
          }
          setSelectedCaseId(caseId);
          setAutoDetected(true);
        } catch (err) {
          console.error('Error loading case context:', err);
        }
      };
      loadCaseContext();
    }
  }, [isOpen, location.pathname, autoDetected]);

  // Load cases when client changes
  useEffect(() => {
    if (!selectedClientId) {
      setCases([]);
      if (!autoDetected) {
        setSelectedCaseId('');
      }
      return;
    }
    const loadCases = async () => {
      setLoadingCases(true);
      try {
        const res = await caseService.getCasesByClient(selectedClientId);
        setCases(res.data || []);
      } catch (err) {
        console.error('Error loading cases:', err);
        setCases([]);
      } finally {
        setLoadingCases(false);
      }
    };
    loadCases();
  }, [selectedClientId, autoDetected]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split('T')[0];
      setForm({
        event_date: today,
        event_time: '',
        include_time: false,
        title: '',
        content: '',
        note_type: 'general',
        is_pinned: false,
      });
      setError(null);
      setSaving(false);
      setAutoDetected(false);
      setSelectedClientId('');
      setSelectedClientLabel(null);
      setSelectedCaseId('');
      setCases([]);
    }
  }, [isOpen]);

  // Focus content when modal opens
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setTimeout(() => contentRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCaseId) {
      setError('Please select a client and case');
      return;
    }
    if (!form.event_date) {
      setError('Date is required');
      return;
    }
    if (!form.content.trim()) {
      setError('Note content is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const noteData = {
        event_date: form.event_date,
        event_time: form.include_time && form.event_time ? `${form.event_time}:00` : null,
        title: form.title.trim() || null,
        content: form.content.trim(),
        note_type: form.note_type,
        is_pinned: form.is_pinned,
      };

      await caseNotesService.createNote(selectedCaseId, noteData);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating note:', err);
      setError(err.response?.data?.detail || 'Failed to create note');
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropClick}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add Note</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
              <AsyncSearchableSelect
                value={selectedClientId}
                onChange={(val) => {
                  setSelectedClientId(val);
                  setSelectedCaseId('');
                }}
                onSearch={searchClientsAsync}
                placeholder="Select a client"
                searchPlaceholder="Search clients..."
                selectedLabel={selectedClientLabel}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Case *</label>
              <select
                value={selectedCaseId}
                onChange={(e) => setSelectedCaseId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                disabled={!selectedClientId || loadingCases}
              >
                <option value="">
                  {!selectedClientId ? 'Select a client first' : loadingCases ? 'Loading cases...' : 'Select a case'}
                </option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>{c.case_number ? `${c.case_number} - ` : ''}{c.title}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date *
              </label>
              <input
                type="date"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="h-4 w-4 inline mr-1" />
                Time
                <span className="text-gray-400 text-xs ml-1">(optional)</span>
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={form.include_time}
                  onChange={(e) => setForm({ ...form, include_time: e.target.checked })}
                  className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                />
                <input
                  type="time"
                  value={form.event_time}
                  onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                  disabled={!form.include_time}
                  className={`flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${
                    !form.include_time ? 'bg-gray-100 text-gray-500' : ''
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
              <span className="text-gray-400 text-xs ml-1">(optional)</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Brief title for the note"
              maxLength={200}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note *
            </label>
            <textarea
              ref={contentRef}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="What happened? Record observations, decisions, or other important details..."
              rows={5}
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Tag className="h-4 w-4 inline mr-1" />
                Category
              </label>
              <select
                value={form.note_type}
                onChange={(e) => setForm({ ...form, note_type: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                {noteTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_pinned}
                  onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })}
                  className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 mr-2"
                />
                <Pin className="h-4 w-4 mr-1 text-gray-600" />
                <span className="text-sm text-gray-700">Pin to top</span>
              </label>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Add Note'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAddNoteModal;
